import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";

import {
  IOrderRefundBalance,
  PostCalculateRefund,
  PostRefund,
  TRefundType,
} from "api/rpc/2024-04/facilityAdmin/order/refund";

import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { IOrder, IOrderLineItem, IOrderTaxLine, IOrderTransaction } from "redux/reducers/models/order";

import { capitalize, delay } from "helpers/Helpers";
import { LocaleCurrency } from "helpers/Locale";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useStripeTerminalPayment } from "hooks/useStripeTerminalPayment/useStripeTerminalPayment";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import { ButtonNew as Button } from "components/buttonNew";
import { IAuthState } from "redux/reducers/auth";
import { GetOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import RefundTypeModal from "pages/secure/facility/order/RefundTypeModal";

interface IRefundProps {
  authStore: IAuthState;
}

type TAllocationBucket = "overpayment" | "tip" | "service_fee";

/** Reserve refundable liabilities first; the remainder is order overpayment. */
const ALLOCATION_PRIORITY: TAllocationBucket[] = ["tip", "service_fee", "overpayment"];

interface IOrderState {
  order: IOrder;
  line_items: IOrderLineItem;
  transactions: IOrderTransaction[];
  total_tax: number;
  total_tip: number;
  total_service_fee: number;
  subtotal_price: number;
  total_price: number;
  tax_lines: IOrderTaxLine[];
  calculated_refund_transactions: Record<string, any>[];
  refund_line_items: {
    line_item_id: number;
    refund_quantity: number;
    refund_amount: number;
  }[];
  refund_tip_amount: number;
  refund_service_fee_amount: number;
  refund_overpayment_amount: number;
  refundable_tip: number;
  refundable_service_fee: number;
  order_balance: IOrderRefundBalance | null;
  overpayment_amount: number;
}

export default function Refund(props: IRefundProps) {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { orderId } = useParams<{ orderId: string }>();
  const mounted = useRef(false);
  const { getCurrencySymbol } = useCurrencyFacility();

  const { authStore } = props;

  const dispatch = useAppDispatch();

  const terminalStore = useAppSelector(store => store.terminalStore);
  const { StripeTerminalRefundSheet, processRefund } = useStripeTerminalPayment();

  function refundTypeFromSearch(search: string): TRefundType | null {
    const type = new URLSearchParams(search).get("type");
    return type === "item_return" || type === "payment_adjustment" ? type : null;
  }

  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [refundType, setRefundType] = useState<TRefundType | null>(() => refundTypeFromSearch(location.search));
  const [refundTypeModalOpen, setRefundTypeModalOpen] = useState(() => !refundTypeFromSearch(location.search));
  const [paymentNote, setPaymentNote] = useState("");
  const [calculateState, setCalculateState] = useState({ index: 0, quantity: "" });
  const [orderState, setOrderState] = useState<IOrderState>({
    order: null,
    line_items: null,
    transactions: null,
    total_tax: null,
    total_tip: null,
    total_service_fee: null,
    subtotal_price: null,
    total_price: null,
    tax_lines: null,
    calculated_refund_transactions: null,
    refund_line_items: null,
    refund_tip_amount: 0,
    refund_service_fee_amount: 0,
    refund_overpayment_amount: 0,
    refundable_tip: 0,
    refundable_service_fee: 0,
    order_balance: null,
    overpayment_amount: 0,
  });

  useEffect(() => {
    void loadOrder();
  }, []);

  useEffect(() => {
    const nextType = refundTypeFromSearch(location.search);
    if (nextType && nextType !== refundType) {
      selectIntent(nextType);
      setRefundTypeModalOpen(false);
    } else if (!nextType) {
      setRefundType(null);
      setRefundTypeModalOpen(true);
    }
  }, [location.search]);

  useEffect(() => {
    let active = true;
    let timeout: NodeJS.Timeout = null;
    if (active === true && refundType === "item_return") {
      timeout = global.setTimeout(() => {
        try {
          if (orderState.order) {
            void calculateRefund();
          }
        } catch (error) {
          console.log("err", error);
        }
        return;
      }, 1000);
    }
    return () => {
      active = false;
      setIsCalculating(true);
      clearTimeout(timeout);
    };
  }, [calculateState.quantity, calculateState.index, refundType]);

  useEffect(() => {
    if (mounted.current === true && refundType === "item_return") {
      void calculateRefund();
    } else {
      mounted.current = true;
    }
  }, [orderState?.refund_tip_amount, orderState?.refund_service_fee_amount, refundType]);

  async function loadOrder() {
    const orderRes = await GetOrder({ id: Number(orderId), extended: true, refund: true }, true);
    if (orderRes.status !== StatusCode.OK) {
      return;
    }

    const order = orderRes.data[0];
    const refund_line_items: { line_item_id: number; refund_quantity: number; refund_amount: number }[] = [];

    order.line_items.forEach((line_item: Record<string, any>) => {
      const refund_line_item = {
        line_item_id: line_item.id,
        refund_quantity: 0,
        refund_amount: line_item.subtotal_price,
      };
      refund_line_items.push(refund_line_item);
    });

    const calculateRes = await PostCalculateRefund(
      {
        order_id: order.id,
        refund_type: "payment_adjustment",
        refund_line_items,
        refund_transactions: order.transactions,
      },
      true,
    );

    if (calculateRes.status !== StatusCode.OK) {
      return;
    }

    refund_line_items.forEach((line_item, i) => {
      if (calculateRes.data.refund_line_items?.[i]) {
        refund_line_items[i] = {
          ...line_item,
          refund_amount: calculateRes.data.refund_line_items[i].refund_amount,
        };
      }
    });

    const filtered_transactions = order.transactions?.filter(
      (filteredTransaction: IOrderTransaction) =>
        filteredTransaction.kind === "sale" || filteredTransaction.kind === "capture",
    );

    const seededTransactions = (calculateRes.data.refund_transactions || []).map((txn: Record<string, any>) => ({
      ...txn,
      amount: 0,
    }));

    setOrderState(prevState => ({
      ...prevState,
      order: order,
      line_items: order.line_items,
      transactions: filtered_transactions,
      total_tax: calculateRes.data.total_tax,
      subtotal_price: calculateRes.data.subtotal_price,
      total_price: calculateRes.data.total_price,
      total_tip: calculateRes?.data?.total_tip,
      total_service_fee: calculateRes?.data?.total_service_fee,
      calculated_refund_transactions: seededTransactions,
      refund_line_items,
      tax_lines: calculateRes.data.tax_lines,
      order_balance: calculateRes.data.order_balance ?? null,
      overpayment_amount: calculateRes.data.overpayment_amount ?? 0,
      refundable_tip: Number(calculateRes.data.refundable_tip ?? 0),
      refundable_service_fee: Number(calculateRes.data.refundable_service_fee ?? 0),
    }));
  }

  async function calculateRefund() {
    if (!orderState.order || refundType !== "item_return") {
      return;
    }

    const calculateRes = await PostCalculateRefund(
      {
        order_id: orderState.order?.id,
        refund_type: "item_return",
        refund_line_items: orderState.refund_line_items,
        refund_transactions: orderState.transactions,
        refund_tip_amount: orderState.refund_tip_amount,
        refund_service_fee_amount: orderState.refund_service_fee_amount,
      },
      true,
    );

    if (calculateRes.status !== StatusCode.OK) {
      dispatch(showError(String(calculateRes.data)));
      console.log(calculateRes);
      return;
    }

    const refund_line_items = orderState.refund_line_items;
    calculateRes.data.refund_line_items?.forEach((item: Record<string, any>) => {
      const matchIndex = refund_line_items.findIndex(line => line.line_item_id === item.line_item_id);
      if (matchIndex >= 0) {
        refund_line_items[matchIndex].refund_amount = item.refund_amount;
      }
    });

    setIsCalculating(false);
    setOrderState(prevState => ({
      ...prevState,
      calculated_refund_transactions: calculateRes.data.refund_transactions,
      total_tax: calculateRes.data.total_tax,
      subtotal_price: calculateRes.data.subtotal_price,
      total_tip: calculateRes?.data?.total_tip,
      total_service_fee: calculateRes?.data?.total_service_fee,
      total_price: calculateRes.data.total_price,
      refund_line_items,
      tax_lines: calculateRes.data.tax_lines,
      order_balance: calculateRes.data.order_balance ?? prevState.order_balance,
      overpayment_amount: calculateRes.data.overpayment_amount ?? prevState.overpayment_amount,
      refundable_tip: Number(calculateRes.data.refundable_tip ?? prevState.refundable_tip),
      refundable_service_fee: Number(calculateRes.data.refundable_service_fee ?? prevState.refundable_service_fee),
    }));
  }

  function getInteracTransactions() {
    const terminal_transactions: Array<IOrderTransaction> = [];
    let refund_transactions = [...(orderState.calculated_refund_transactions || [])];
    orderState.transactions?.forEach((transaction, i) => {
      if (transaction.payment_type === "interac" && Number(orderState.calculated_refund_transactions[i]?.amount) > 0) {
        terminal_transactions.push({
          ...transaction,
          amount: Number(orderState.calculated_refund_transactions[i].amount),
        });
      } else if (
        transaction.payment_type === "interac" &&
        (!orderState.calculated_refund_transactions[i]?.amount ||
          Number(orderState.calculated_refund_transactions[i].amount) == 0)
      ) {
        refund_transactions = refund_transactions.filter(
          refund_transaction => refund_transaction.parent_id !== transaction.id,
        );
      }
    });

    return [terminal_transactions, refund_transactions];
  }

  async function handleRefund() {
    if (!refundType) {
      return;
    }

    const transactions = getInteracTransactions();
    const [terminal_transactions] = transactions;
    let [, refund_transactions] = transactions;
    let interacRefundSuccessful = false;

    refund_transactions = refund_transactions.filter(refund => Number(refund.amount) > 0);

    if (refundType === "item_return") {
      const hasQty = orderState.refund_line_items?.some(item => item.refund_quantity > 0);
      if (!hasQty) {
        dispatch(showError(t("Select at least one item quantity to return")));
        return;
      }
    }

    if (refund_transactions.length <= 0) {
      dispatch(showError(t("Refund amount must be greater than 0")));
      return;
    }

    if (refundType === "payment_adjustment" && !isAllocated) {
      dispatch(showError(t("Tip, service fee, and overpayment must equal the refund payment total")));
      return;
    }

    if (terminal_transactions.length > 0) {
      if (terminalStore?.reader) {
        for (const transaction of terminal_transactions) {
          dispatch(enqueue());
          await delay(2000);
          dispatch(dequeue());

          const refundRes = await processRefund(Number(transaction.amount), String(transaction.payment_intent_id));
          if (refundRes === "success") {
            interacRefundSuccessful = true;
            dispatch(showSuccess(t("secure.facility.order.refund.004")));
          } else {
            refund_transactions = refund_transactions.filter(
              refund_transaction => refund_transaction.parent_id !== transaction.id,
            );
          }
        }
      } else {
        dispatch(showError(t("secure.facility.order.refund.006")));
        return;
      }
    }

    if ((terminal_transactions.length > 0 && interacRefundSuccessful) || terminal_transactions.length === 0) {
      const refundRes = await PostRefund(
        {
          order_id: orderState.order.id,
          refund_type: refundType,
          refund_line_items: refundType === "item_return" ? orderState.refund_line_items : [],
          refund_transactions: refund_transactions,
          refund_tip_amount: orderState.refund_tip_amount,
          refund_service_fee_amount: orderState.refund_service_fee_amount,
          refund_overpayment_amount:
            refundType === "payment_adjustment" ? orderState.refund_overpayment_amount : undefined,
          note: refundType === "payment_adjustment" ? paymentNote || undefined : undefined,
        },
        true,
      );
      if (refundRes.status !== StatusCode.OK) {
        dispatch(showError(String(refundRes.data) || t("secure.facility.order.refund.007")));
        return;
      }
      dispatch(showSuccess(t("secure.facility.order.refund.004")));
      history.push("/admin/order/" + String(orderId));
    }
  }

  function handleQuantityInputChange(event: React.ChangeEvent<HTMLInputElement>, item_quantity: number, index: number) {
    const { value } = event.target;
    const refundLineItems = [...orderState.refund_line_items];
    if (Number(value) >= 0 && Number(value) <= item_quantity) {
      refundLineItems[index] = { ...refundLineItems[index], refund_quantity: Number(value) };
      setOrderState(prevState => ({ ...prevState, refund_line_items: refundLineItems }));
      setCalculateState(prevState => ({ ...prevState, index, quantity: value }));
    }
  }

  function handleRefundAmountInputChange(event: React.ChangeEvent<HTMLInputElement>, index: number) {
    if (refundType !== "payment_adjustment") {
      return;
    }
    const { value } = event.target;
    const transactions = [...(orderState.calculated_refund_transactions || [])];
    if (Number(value) >= 0 && Number(value) <= Number(transactions[index].maximum_refundable)) {
      transactions[index] = { ...transactions[index], amount: value };
      const nextPaymentTotal = transactions.reduce((sum, txn) => sum + Number(txn.amount || 0), 0);
      const allocation = buildAllocation(nextPaymentTotal, []);
      setOrderState(prevState => ({
        ...prevState,
        calculated_refund_transactions: transactions,
        refund_overpayment_amount: allocation.overpayment,
        refund_tip_amount: allocation.tip,
        refund_service_fee_amount: allocation.service_fee,
      }));
    }
  }

  /** Payment out remaining after the currently selected tip and service fee. */
  function overpaymentAllocationMax(
    paymentOut: number,
    tipAmount = Number(orderState.refund_tip_amount || 0),
    serviceFeeAmount = Number(orderState.refund_service_fee_amount || 0),
  ) {
    return Math.max(0, Math.round((paymentOut - tipAmount - serviceFeeAmount) * 100) / 100);
  }

  /** How much each allocation bucket can absorb. */
  function allocationCaps(paymentOut: number) {
    return {
      overpayment: Math.max(0, paymentOut),
      tip: canRefundTip ? Number(orderState.refundable_tip || 0) : 0,
      service_fee: canRefundServiceFee ? Number(orderState.refundable_service_fee || 0) : 0,
    };
  }

  /**
   * Allocate refundable tip and service fee first, then put the remainder on order
   * overpayment. Buckets listed in `locked` keep the value the user typed and only the
   * rest rebalance around it.
   */
  function buildAllocation(
    paymentOut: number,
    locked: TAllocationBucket[],
    overrides: Partial<Record<TAllocationBucket, number>> = {},
  ) {
    const caps = allocationCaps(paymentOut);
    const current: Record<TAllocationBucket, number> = {
      overpayment: Number(orderState.refund_overpayment_amount || 0),
      tip: Number(orderState.refund_tip_amount || 0),
      service_fee: Number(orderState.refund_service_fee_amount || 0),
      ...overrides,
    };
    const result: Record<TAllocationBucket, number> = { overpayment: 0, tip: 0, service_fee: 0 };
    let remaining = Math.round(Number(paymentOut || 0) * 100) / 100;

    locked.forEach(bucket => {
      result[bucket] = Math.min(Math.max(0, current[bucket]), Math.max(0, caps[bucket]));
      remaining = Math.round((remaining - result[bucket]) * 100) / 100;
    });

    ALLOCATION_PRIORITY.filter(bucket => !locked.includes(bucket)).forEach(bucket => {
      result[bucket] = Math.min(Math.max(0, remaining), caps[bucket]);
      remaining = Math.round((remaining - result[bucket]) * 100) / 100;
    });

    return result;
  }

  function selectIntent(intent: TRefundType) {
    setRefundType(intent);
    setPaymentNote("");
    setOrderState(prev => ({
      ...prev,
      refund_tip_amount: 0,
      refund_service_fee_amount: 0,
      refund_overpayment_amount: 0,
      refund_line_items:
        prev.refund_line_items?.map(item => ({ ...item, refund_quantity: 0 })) ?? prev.refund_line_items,
      calculated_refund_transactions:
        prev.calculated_refund_transactions?.map(txn => ({
          ...txn,
          amount: 0,
        })) ?? prev.calculated_refund_transactions,
      total_price: 0,
      subtotal_price: 0,
      total_tax: 0,
    }));
  }

  function handleRefundTypeSelect(intent: TRefundType) {
    setRefundTypeModalOpen(false);
    if (intent === refundType) {
      selectIntent(intent);
      return;
    }
    history.replace("/admin/order/" + String(orderId) + "/refund?type=" + intent);
  }

  function openRefundTypeModal() {
    setRefundTypeModalOpen(true);
  }

  function determineStepValue(quantity: number) {
    const quantityStep = quantity % 1;
    return quantityStep === 0 ? 1 : quantityStep;
  }

  function closeRefundTypeModal() {
    if (!refundType) {
      history.push("/admin/order/" + String(orderId));
      return;
    }
    setRefundTypeModalOpen(false);
  }

  const paymentTotal = (orderState.calculated_refund_transactions || []).reduce(
    (sum, txn) => sum + Number(txn.amount || 0),
    0,
  );
  const salesTotal = Number(orderState.total_price || 0);
  const isBalanced = refundType === "item_return" && Math.abs(paymentTotal - salesTotal) < 0.01;

  const allocatedTotal =
    Number(orderState.refund_tip_amount || 0) +
    Number(orderState.refund_service_fee_amount || 0) +
    Number(orderState.refund_overpayment_amount || 0);
  const allocationDifference = Math.round((paymentTotal - allocatedTotal) * 100) / 100;
  const isAllocated = paymentTotal > 0 && Math.abs(allocationDifference) < 0.01;
  // Overpayment returned beyond what the order was actually overpaid leaves it underpaid.
  const underpaymentCreated =
    Math.round((Number(orderState.refund_overpayment_amount || 0) - Number(orderState.overpayment_amount || 0)) * 100) /
    100;
  const leavesOrderUnderpaid = refundType === "payment_adjustment" && underpaymentCreated > 0.009;

  const disableRefundBtn =
    !refundType ||
    orderState.calculated_refund_transactions?.every(transaction => Number(transaction.maximum_refundable) === 0) ||
    (refundType === "item_return" &&
      (!orderState.refund_line_items?.some(item => item.refund_quantity > 0) || !isBalanced)) ||
    (refundType === "payment_adjustment" && (paymentTotal <= 0 || !isAllocated)) ||
    isCalculating;

  const canRefundTip = authStore?.user?.permissions?.orders_refund_tip && Number(orderState.refundable_tip) > 0;
  const canRefundServiceFee =
    authStore?.user?.permissions?.orders_refund_service_fee && Number(orderState.refundable_service_fee) > 0;

  /** Snap a typed amount into range so the field never keeps a value the API would reject. */
  function clampAmount(value: string, max: number) {
    if (value === "" || Number.isNaN(Number(value))) {
      return 0;
    }
    return Math.min(Math.max(0, Number(value)), Math.max(0, Number(max || 0)));
  }

  function handleRefundTipAmountChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    const nextTip = clampAmount(value, Number(orderState.refundable_tip));
    if (refundType !== "payment_adjustment") {
      setOrderState(prevState => ({ ...prevState, refund_tip_amount: nextTip }));
      return;
    }
    // Money-only: honour the typed tip and rebalance the other buckets to the payment out.
    const allocation = buildAllocation(paymentTotal, ["tip"], { tip: nextTip });
    setOrderState(prevState => ({
      ...prevState,
      refund_tip_amount: allocation.tip,
      refund_service_fee_amount: allocation.service_fee,
      refund_overpayment_amount: allocation.overpayment,
    }));
  }

  function handleRefundServiceFeeAmountChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    const nextServiceFee = clampAmount(value, Number(orderState.refundable_service_fee));
    if (refundType !== "payment_adjustment") {
      setOrderState(prevState => ({ ...prevState, refund_service_fee_amount: nextServiceFee }));
      return;
    }
    const allocation = buildAllocation(paymentTotal, ["service_fee"], { service_fee: nextServiceFee });
    setOrderState(prevState => ({
      ...prevState,
      refund_tip_amount: allocation.tip,
      refund_service_fee_amount: allocation.service_fee,
      refund_overpayment_amount: allocation.overpayment,
    }));
  }

  function handleRefundOverpaymentAmountChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    const nextOverpayment = clampAmount(value, overpaymentAllocationMax(paymentTotal));
    const allocation = buildAllocation(paymentTotal, ["overpayment"], { overpayment: nextOverpayment });
    setOrderState(prevState => ({
      ...prevState,
      refund_tip_amount: allocation.tip,
      refund_service_fee_amount: allocation.service_fee,
      refund_overpayment_amount: allocation.overpayment,
    }));
  }

  /** Tip / service fee amount inputs, capped at what no earlier refund has reversed. */
  function renderAllocationInputs() {
    if (!canRefundTip && !canRefundServiceFee && refundType !== "payment_adjustment") {
      return null;
    }

    const tipInput = canRefundTip ? (
      <Input
        label={t("Refund Tip")}
        value={orderState.refund_tip_amount}
        onChange={handleRefundTipAmountChange}
        prefix={getCurrencySymbol(orderState.order?.currency) ?? "$"}
        suffix={"/ " + String(orderState.refundable_tip)}
        type="number"
      />
    ) : null;
    const serviceFeeInput = canRefundServiceFee ? (
      <Input
        label={t("Refund Service Fee")}
        value={orderState.refund_service_fee_amount}
        onChange={handleRefundServiceFeeAmountChange}
        prefix={getCurrencySymbol(orderState.order?.currency) ?? "$"}
        suffix={"/ " + String(orderState.refundable_service_fee)}
        type="number"
      />
    ) : null;
    const overpaymentInput =
      refundType === "payment_adjustment" ? (
        <Input
          label={t("Order overpayment")}
          value={orderState.refund_overpayment_amount}
          onChange={handleRefundOverpaymentAmountChange}
          prefix={getCurrencySymbol(orderState.order?.currency) ?? "$"}
          suffix={"/ " + String(overpaymentAllocationMax(paymentTotal))}
          type="number"
        />
      ) : null;

    return (
      <div className="flex flex-col gap-4">
        {refundType === "payment_adjustment" ? (
          <>
            {overpaymentInput}
            {tipInput}
            {serviceFeeInput}
          </>
        ) : (
          <>
            {tipInput}
            {serviceFeeInput}
          </>
        )}
      </div>
    );
  }

  function renderPaymentInputs() {
    return (
      <div className="flex flex-col gap-4">
        {orderState.transactions?.map((transaction: IOrderTransaction, i: number) => (
          <Input
            key={transaction.id ?? i}
            label={
              transaction?.payment_type ? capitalize(transaction.payment_type) : capitalize(transaction?.payment_method)
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleRefundAmountInputChange(event, i)}
            value={orderState.calculated_refund_transactions?.[i]?.amount ?? 0}
            prefix={getCurrencySymbol(orderState.order?.currency) ?? "$"}
            suffix={"/ " + String(orderState.calculated_refund_transactions?.[i]?.maximum_refundable ?? 0)}
            type="number"
          />
        ))}
      </div>
    );
  }

  function renderRefundPayments() {
    return (
      <Card.SubSection>
        <p className="font-semibold mb-3">{t("Payment out")}</p>
        <div className="flex flex-col gap-2">
          {orderState.transactions?.map((transaction: IOrderTransaction, i: number) => (
            <div key={transaction.id ?? i} className="flex items-center justify-between">
              <p>
                {transaction?.payment_type
                  ? capitalize(transaction.payment_type)
                  : capitalize(transaction?.payment_method)}
              </p>
              <LocaleCurrency
                currency={orderState.order?.currency}
                amount={Number(orderState.calculated_refund_transactions?.[i]?.amount ?? 0)}
              />
            </div>
          ))}
        </div>
      </Card.SubSection>
    );
  }

  function renderBalanceSummary() {
    const balance = orderState.order_balance;
    if (!balance) {
      return null;
    }

    let balanceLabel = t("Balanced");
    if (balance.overpayment_amount > 0) {
      balanceLabel = t("Overpayment");
    } else if (balance.underpayment_amount > 0) {
      balanceLabel = t("Underpayment");
    }

    return (
      <Card>
        <Card.Section title={t("Order balance")}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-lg">{t("Active sales")}</p>
            <LocaleCurrency currency={orderState.order?.currency} amount={balance.active_sales} />
          </div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-lg">{t("Active payments")}</p>
            <LocaleCurrency currency={orderState.order?.currency} amount={balance.active_payments} />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-lg font-semibold">{balanceLabel}</p>
            <LocaleCurrency
              currency={orderState.order?.currency}
              amount={balance.overpayment_amount || balance.underpayment_amount || 0}
            />
          </div>
        </Card.Section>
      </Card>
    );
  }

  function renderTenderAvailability() {
    return (
      <Card.SubSection>
        <span className="font-semibold">{t("Available by tender")}</span>
        {orderState.transactions?.map((transaction: IOrderTransaction, i: number) => {
          const max = orderState.calculated_refund_transactions?.[i]?.maximum_refundable ?? 0;
          return (
            <div key={i} className="flex items-center justify-between mt-2">
              <p>
                {transaction?.payment_type
                  ? capitalize(transaction?.payment_type)
                  : capitalize(transaction?.payment_method)}
              </p>
              <LocaleCurrency currency={orderState.order?.currency} amount={max} />
            </div>
          );
        })}
      </Card.SubSection>
    );
  }

  if (!refundType) {
    return (
      <>
        <Page
          narrow
          title={t("secure.facility.order.refund.010") + (orderState.order?.name ?? "")}
          breadcrumbs={[
            { prefix: true, label: t("secure.facility.order.refund.011"), url: "/admin/order/" + String(orderId) },
          ]}
        >
          <Page.Section full>{renderBalanceSummary()}</Page.Section>
        </Page>
        <RefundTypeModal open={refundTypeModalOpen} onCancel={closeRefundTypeModal} onSelect={handleRefundTypeSelect} />
        <StripeTerminalRefundSheet />
      </>
    );
  }

  return (
    <>
      <Page
        narrow
        splitLayout
        title={t("secure.facility.order.refund.010") + orderState.order?.name}
        breadcrumbs={[
          { prefix: true, label: t("secure.facility.order.refund.011"), url: "/admin/order/" + String(orderId) },
        ]}
      >
        <Page.Section twoThirds>
          <div className="mb-4">
            <Button type="text" onClick={openRefundTypeModal}>
              {t("Change refund type")}
            </Button>
          </div>

          {refundType === "item_return" ? (
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold">{t("secure.facility.order.refund.012")}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {t("Choose the item quantities and any order-level amounts to return.")}
                </p>
              </div>

              <Card.Section>
                {orderState.order?.line_items &&
                  orderState.order.line_items?.map((item: any, index: number) => {
                    return (
                      <Card.SubSection key={index}>
                        <div className="order-line-item">
                          <div>
                            <p className="text-lg font-semibold">{item.product_title}</p>
                            {item.product_title !== item.variant_title ? (
                              <p className="text-sm text-gray-600">{item.variant_title}</p>
                            ) : null}
                          </div>
                          <div className="text-right">
                            <Input
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                handleQuantityInputChange(e, Number(item.refundable_quantity), index)
                              }
                              value={orderState.refund_line_items[index].refund_quantity}
                              suffix={"/ " + String(item.refundable_quantity)}
                              disabled={item.refundable_quantity === 0 || item.non_refundable ? true : false}
                              type="number"
                              step={determineStepValue(Number(item.refundable_quantity))}
                            />
                            <p className="text-lg text-gray-600">
                              x <LocaleCurrency currency={orderState.order?.currency} amount={item.price} />
                            </p>
                          </div>
                          <div className="text-right text-lg">
                            <LocaleCurrency
                              currency={orderState.order?.currency}
                              amount={orderState.refund_line_items[index].refund_amount}
                            />
                          </div>
                        </div>
                      </Card.SubSection>
                    );
                  })}
                {canRefundTip || canRefundServiceFee ? (
                  <Card.SubSection>
                    <div className="mb-4">
                      <p className="font-semibold">{t("Additional refund amounts")}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {t("Optionally include part or all of the remaining tip and service fee.")}
                      </p>
                    </div>
                    {renderAllocationInputs()}
                  </Card.SubSection>
                ) : null}
                <Card.SubSection>
                  <Button onClick={handleRefund} block size="medium" type="primary" disabled={disableRefundBtn}>
                    {t("secure.facility.order.refund.019")}
                  </Button>
                </Card.SubSection>
              </Card.Section>
            </Card>
          ) : (
            <Card>
              <Card.Section title={t("Return money only")}>
                <div className="flex flex-col gap-6">
                  <div>
                    <p className="text-sm text-gray-600">
                      {t("Return money without changing item quantities or department sales.")}
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">{t("1. Payment out")}</p>
                    <p className="text-sm text-gray-600 mt-1 mb-4">
                      {t("Enter how much to return to each original payment method.")}
                    </p>
                    {renderPaymentInputs()}
                  </div>

                  <div>
                    <p className="font-semibold">{t("2. Allocate the refund")}</p>
                    <p className="text-sm text-gray-600 mt-1 mb-4">
                      {t(
                        "Tip and service fee are allocated first, and the remainder goes to order overpayment. Lower either amount to move those funds to overpayment.",
                      )}
                    </p>
                    {renderAllocationInputs()}

                    <div
                      className={
                        "mt-4 rounded p-4 " +
                        (isAllocated ? "bg-green-50 text-green-800" : "bg-yellow-50 text-yellow-900")
                      }
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-semibold">{t("Allocated")}</p>
                        <p className="font-semibold">
                          <LocaleCurrency currency={orderState.order?.currency} amount={allocatedTotal} />
                          {" / "}
                          <LocaleCurrency currency={orderState.order?.currency} amount={paymentTotal} />
                        </p>
                      </div>
                      {!isAllocated && paymentTotal > 0 ? (
                        <p className="text-sm mt-2">
                          {allocationDifference > 0 ? t("Still to allocate") : t("Over allocated")}:{" "}
                          <LocaleCurrency
                            currency={orderState.order?.currency}
                            amount={Math.abs(allocationDifference)}
                          />
                        </p>
                      ) : null}
                      {leavesOrderUnderpaid ? (
                        <p className="text-sm mt-2">
                          {t("Returns")}{" "}
                          <LocaleCurrency currency={orderState.order?.currency} amount={underpaymentCreated} />{" "}
                          {t("more than this order was overpaid, so it will be left underpaid by that amount.")}
                        </p>
                      ) : null}
                      {paymentTotal <= 0 ? (
                        <p className="text-sm mt-2">{t("Enter the payment out before allocating it.")}</p>
                      ) : null}
                    </div>
                  </div>

                  <div>
                    <p className="font-semibold">{t("3. Add a reason")}</p>
                    <p className="text-sm text-gray-600 mt-1 mb-4">
                      {t("Add a note so staff can understand why this money was returned.")}
                    </p>
                    <Input
                      label={t("Reason (optional)")}
                      value={paymentNote}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPaymentNote(e.target.value)}
                      placeholder={t("Overpayment, tip return, goodwill...")}
                    />
                  </div>

                  <Button onClick={handleRefund} block size="medium" type="primary" disabled={disableRefundBtn}>
                    {t("secure.facility.order.refund.019")}
                  </Button>
                </div>
              </Card.Section>
            </Card>
          )}
        </Page.Section>
        <Page.Section oneThird>
          {renderBalanceSummary()}
          <Card>
            <Card.Section title={t("secure.facility.order.refund.013")}>
              {refundType === "item_return" ? (
                <>
                  <Card.SubSection>
                    <div className="flex items-center justify-between">
                      <p className="text-lg">{t("secure.facility.order.refund.014")}</p>
                      <LocaleCurrency currency={orderState.order?.currency} amount={orderState.subtotal_price} />
                    </div>
                  </Card.SubSection>
                  <Card.SubSection>
                    {orderState.tax_lines?.map((tax_line, i) => {
                      return (
                        <div key={i} className="flex items-center justify-between">
                          <p className="text-lg">{tax_line.title}</p>
                          <LocaleCurrency currency={orderState.order?.currency} amount={tax_line.price} />
                        </div>
                      );
                    })}

                    <div className="flex items-center justify-between">
                      <p className="text-lg">{t("secure.facility.order.refund.016")}</p>
                      <LocaleCurrency currency={orderState.order?.currency} amount={orderState.total_tax} />
                    </div>
                  </Card.SubSection>
                  {orderState.refund_tip_amount > 0 ? (
                    <Card.SubSection>
                      <div className="flex items-center justify-between">
                        <p className="text-lg">{t("Total Tip")}</p>
                        <LocaleCurrency currency={orderState.order?.currency} amount={orderState.refund_tip_amount} />
                      </div>
                    </Card.SubSection>
                  ) : null}
                  {orderState.refund_service_fee_amount > 0 ? (
                    <Card.SubSection>
                      <div className="flex items-center justify-between">
                        <p className="text-lg">{t("Service Fee")}</p>
                        <LocaleCurrency
                          currency={orderState.order?.currency}
                          amount={orderState.refund_service_fee_amount}
                        />
                      </div>
                    </Card.SubSection>
                  ) : null}

                  <Card.SubSection>
                    <div className="flex items-center justify-between">
                      <p className="text-lg font-semibold">{t("secure.facility.order.refund.017")}</p>
                      <LocaleCurrency currency={orderState.order?.currency} amount={orderState.total_price} />
                    </div>
                  </Card.SubSection>
                </>
              ) : (
                <Card.SubSection>
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-semibold">{t("Refund amount")}</p>
                    <LocaleCurrency currency={orderState.order?.currency} amount={paymentTotal} />
                  </div>
                  {orderState.refund_tip_amount > 0 ? (
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-lg">{t("Total Tip")}</p>
                      <LocaleCurrency currency={orderState.order?.currency} amount={orderState.refund_tip_amount} />
                    </div>
                  ) : null}
                  {orderState.refund_service_fee_amount > 0 ? (
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-lg">{t("Service Fee")}</p>
                      <LocaleCurrency
                        currency={orderState.order?.currency}
                        amount={orderState.refund_service_fee_amount}
                      />
                    </div>
                  ) : null}
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-lg">{t("Overpayment")}</p>
                    <LocaleCurrency
                      currency={orderState.order?.currency}
                      amount={orderState.refund_overpayment_amount}
                    />
                  </div>
                </Card.SubSection>
              )}

              {renderTenderAvailability()}
              {renderRefundPayments()}

              {refundType === "item_return" ? (
                <Card.SubSection>
                  <p className="text-sm">
                    {t("Refunding")} <LocaleCurrency currency={orderState.order?.currency} amount={salesTotal} />{" "}
                    {t("sales")} · <LocaleCurrency currency={orderState.order?.currency} amount={paymentTotal} />{" "}
                    {t("payments")} · {isBalanced ? t("Balanced") : t("Not balanced")}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">{t("Sales and payments reverse together.")}</p>
                </Card.SubSection>
              ) : (
                <Card.SubSection>
                  <p className="text-sm text-gray-600">
                    {t("Payment out is offset by the Customer Overpayments account.")}
                  </p>
                </Card.SubSection>
              )}
            </Card.Section>
          </Card>
        </Page.Section>
      </Page>
      <RefundTypeModal open={refundTypeModalOpen} onCancel={closeRefundTypeModal} onSelect={handleRefundTypeSelect} />
      <StripeTerminalRefundSheet />
    </>
  );
}
